import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar/Navbar';
import Footer from './components/Footer/Footer';
import PortalLayout from './components/PortalLayout/PortalLayout';
import { PortalRoleProvider } from './contexts/PortalRoleContext';
import Home from './pages/Home/Home';
import Courses from './pages/Courses/Courses';
import Timetable from './pages/Timetable/Timetable';
import Team from './pages/Team/Team';
import About from './pages/About/About';
import Contact from './pages/Contact/Contact';
import GlobalStyle from './styles/GlobalStyle';
import Assessment from './pages/Assessment/Assessment';
import Careers from './pages/Careers/Careers';
import StudentRewards from './pages/StudentRewards/StudentRewards';
import OfficialResources from './pages/OfficialResources/OfficialResources';
import Login from './pages/Login/Login';
import PortalHome from './pages/Portal/PortalHome';
import UserManagement from './pages/Portal/UserManagement';
import TeacherAvailability from './pages/Portal/TeacherAvailability';
import StudentManagement from './pages/Portal/StudentManagement';
import CampusManagement from './pages/Portal/CampusManagement';
import AiChat from './pages/Portal/AiChat';
import CourseGroups from './pages/Portal/CourseGroups';
import MyCourseGroups from './pages/Portal/MyCourseGroups';
import MaterialsManagement from './pages/Portal/MaterialsManagement';
import BillingManagement from './pages/Portal/BillingManagement';
import MaterialAccessAudit from './pages/Portal/MaterialAccessAudit';
import PortalLogin from './pages/Portal/PortalLogin';
import UserSettings from './pages/Portal/UserSettings';
import ParentAgreement from './pages/Portal/ParentAgreement';
import TeacherProfile from './pages/Portal/TeacherProfile';
import ParentProfile from './pages/Portal/ParentProfile';
import AdminProfile from './pages/Portal/AdminProfile';
import CampusTimetableTeacher from './pages/Portal/CampusTimetableTeacher';
import PortalTimetable from './pages/Portal/Timetable/Timetable';
import PortalTimetableGuide from './pages/Portal/Timetable/TimetableGuide';
import NotificationCenter from './pages/Portal/NotificationCenter';
import TeachingTips from './pages/Portal/TeachingTips';
import TestManagement from './pages/Portal/TestManagement/TestManagement';
import PortalTestTemplate from './pages/Portal/TestManagement/PortalTestTemplate';
import PortalTestResponse from './pages/Portal/TestManagement/PortalTestResponse';
import PortalMockTestTransformation from './pages/Portal/TestManagement/PortalMockTestTransformation';
import PortalMockTestTransformationTraditional from './pages/Portal/TestManagement/PortalMockTestTransformationTraditional';
import PortalAssessmentScheduleManagement from './pages/Portal/PortalAssessmentScheduleManagement';
import SiteSettings from './pages/Portal/SiteSettings';
import Policy from './pages/Policy/Policy';
import AuthHome from './pages/AuthHome/AuthHome';
import { useAuth } from "react-oidc-context";
import { isStaging } from './utils/envConfig';
// 标注功能真机调试页：只在开发环境注册路由（生产构建不会有这个地址）。
// iPad 通过局域网 IP 访问 dev server 时无法走 Cognito Hosted UI 登录，
// 因此提供一个免登录入口来验证书写/滚动手势，详见该文件顶部注释。
import AnnotationLab from './pages/Dev/AnnotationLab';

// 旧的内部 Dashboard 已停用，功能全部迁移至 Portal。
// 保留这些旧路由仅用于把直接访问/旧书签重定向到对应的 Portal 页面（保留 query 参数，如 ?id=、?cloneFrom=）。
function LegacyRedirect({ to }) {
  const location = useLocation();
  return <Navigate to={`${to}${location.search}`} replace />;
}

function App() {
  const auth = useAuth();

  if (auth.isLoading) {
    return <div>Loading...</div>;
  }

  if (auth.error) {
    // Auto-recover from stale OIDC state (e.g. after config changes or failed login attempts)
    const stateErrors = ['No matching state found in storage', 'state', 'login_required'];
    if (stateErrors.some(e => auth.error.message?.includes(e))) {
      // Clear all oidc-related entries from sessionStorage
      Object.keys(sessionStorage).forEach(key => {
        if (key.startsWith('oidc.')) {
          sessionStorage.removeItem(key);
        }
      });
      // Reload to get a clean state
      window.location.replace('/login');
      return <div>Redirecting to login...</div>;
    }
    return <div>Encountering error... {auth.error.message}</div>;
  }
  return (
    <>
    <GlobalStyle />
    <PortalRoleProvider>
    <Router>
      {isStaging() && (
        <div style={{
          position: 'sticky',
          top: 0,
          zIndex: 9999,
          background: '#ff9800',
          color: '#fff',
          textAlign: 'center',
          padding: '6px 0',
          fontWeight: 'bold',
          fontSize: '14px',
          letterSpacing: '1px',
        }}>
          STAGING ENVIRONMENT
        </div>
      )}
      <Routes>
        {process.env.NODE_ENV === 'development' && (
          <Route path="/dev/annotation-lab" element={<AnnotationLab />} />
        )}

        {/* 公开页面 - 使用普通导航栏 */}
        <Route path="/" element={
          <>
            <Navbar />
            <Home />
            <Footer />
          </>
        } />
        <Route path="/courses" element={
          <>
            <Navbar />
            <Courses />
            <Footer />
          </>
        } />
        <Route path="/timetable" element={
          <>
            <Navbar />
            <Timetable />
            <Footer />
          </>
        } />
        <Route path="/team" element={
          <>
            <Navbar />
            <Team />
            <Footer />
          </>
        } />
        <Route path="/about" element={
          <>
            <Navbar />
            <About />
            <Footer />
          </>
        } />
        <Route path="/contact" element={
          <>
            <Navbar />
            <Contact />
            <Footer />
          </>
        } />
        <Route path="/assessment" element={
          <>
            <Navbar />
            <Assessment />
            <Footer />
          </>
        } />
        <Route path="/careers" element={
          <>
            <Navbar />
            <Careers />
            <Footer />
          </>
        } />
        <Route path="/student-achievements" element={
          <>
            <Navbar />
            <StudentRewards />
            <Footer />
          </>
        } />
        <Route path="/official-resources" element={
          <>
            <Navbar />
            <OfficialResources />
            <Footer />
          </>
        } />
        <Route path="/login" element={
          <>
            <Navbar />
            <Login />
            <Footer />
          </>
        } />
        <Route path="/policy" element={
          <>
            <Navbar />
            <Policy />
            <Footer />
          </>
        } />
        
        {/* 认证后的主页 - 只需登录即可访问 */}
        <Route path="/home" element={<AuthHome />} />

        {/* 旧的内部 Dashboard 路由 - 已停用，重定向到对应的 Portal 页面 */}
        <Route path="/test-authenticated" element={<LegacyRedirect to="/portal" />} />
        <Route path="/dashboard" element={<LegacyRedirect to="/portal/test-management" />} />
        <Route path="/test-template" element={<LegacyRedirect to="/portal/test-template" />} />
        <Route path="/test-response" element={<LegacyRedirect to="/portal/test-response" />} />
        <Route path="/mock-test-transformation" element={<LegacyRedirect to="/portal/mock-test-transformation" />} />
        <Route path="/mock-test-transformation-traditional" element={<LegacyRedirect to="/portal/mock-test-transformation-traditional" />} />
        <Route path="/assessment-schedule-management" element={<LegacyRedirect to="/portal/assessment-schedules" />} />

        {/* Portal页面 - 无需授权的演示版 */}
        <Route path="/portal" element={
          <PortalLayout>
            <PortalHome />
          </PortalLayout>
        } />
        <Route path="/portal/users" element={
          <PortalLayout>
            <UserManagement />
          </PortalLayout>
        } />
        <Route path="/portal/teacher-availability" element={
          <PortalLayout>
            <TeacherAvailability />
          </PortalLayout>
        } />
        <Route path="/portal/students" element={
          <PortalLayout>
            <StudentManagement />
          </PortalLayout>
        } />
        <Route path="/portal/campuses" element={
          <PortalLayout>
            <CampusManagement />
          </PortalLayout>
        } />
        <Route path="/portal/ai-chat" element={
          <PortalLayout>
            <AiChat />
          </PortalLayout>
        } />
        <Route path="/portal/course-groups" element={
          <PortalLayout>
            <CourseGroups />
          </PortalLayout>
        } />
        <Route path="/portal/my-courses" element={
          <PortalLayout>
            <CourseGroups />
          </PortalLayout>
        } />
        <Route path="/portal/my-course-groups" element={
          <PortalLayout>
            <MyCourseGroups />
          </PortalLayout>
        } />
        <Route path="/portal/materials" element={
          <PortalLayout>
            <MaterialsManagement />
          </PortalLayout>
        } />
        <Route path="/portal/billing" element={
          <PortalLayout>
            <BillingManagement />
          </PortalLayout>
        } />
        <Route path="/portal/material-access" element={
          <PortalLayout>
            <MaterialAccessAudit />
          </PortalLayout>
        } />
        <Route path="/portal/teacher-dashboard" element={
          <PortalLayout>
            <PortalHome />
          </PortalLayout>
        } />
        <Route path="/portal/my-children" element={
          <PortalLayout>
            <PortalHome />
          </PortalLayout>
        } />
        <Route path="/portal/my-children-classes" element={
          <PortalLayout>
            <MyCourseGroups role="parent" />
          </PortalLayout>
        } />

        {/* New Portal Pages */}
        <Route path="/portal/login" element={
          <PortalLogin />
        } />
        <Route path="/portal/settings" element={
          <PortalLayout>
            <UserSettings />
          </PortalLayout>
        } />
        <Route path="/portal/parent-agreement" element={
          <ParentAgreement />
        } />
        <Route path="/portal/teacher-profile" element={
          <PortalLayout>
            <TeacherProfile />
          </PortalLayout>
        } />
        <Route path="/portal/parent-profile" element={
          <PortalLayout>
            <ParentProfile />
          </PortalLayout>
        } />
        <Route path="/portal/admin-profile" element={
          <PortalLayout>
            <AdminProfile />
          </PortalLayout>
        } />
        <Route path="/portal/notifications" element={
          <PortalLayout>
            <NotificationCenter />
          </PortalLayout>
        } />
        <Route path="/portal/teaching-tips" element={
          <PortalLayout>
            <TeachingTips />
          </PortalLayout>
        } />

        {/* Assessment / Test Management */}
        <Route path="/portal/test-management" element={
          <PortalLayout>
            <TestManagement />
          </PortalLayout>
        } />
        <Route path="/portal/test-template" element={
          <PortalLayout>
            <PortalTestTemplate />
          </PortalLayout>
        } />
        <Route path="/portal/test-response" element={
          <PortalLayout>
            <PortalTestResponse />
          </PortalLayout>
        } />
        <Route path="/portal/mock-test-transformation" element={
          <PortalLayout>
            <PortalMockTestTransformation />
          </PortalLayout>
        } />
        <Route path="/portal/mock-test-transformation-traditional" element={
          <PortalLayout>
            <PortalMockTestTransformationTraditional />
          </PortalLayout>
        } />
        <Route path="/portal/assessment-schedules" element={
          <PortalLayout>
            <PortalAssessmentScheduleManagement />
          </PortalLayout>
        } />

        {/* Site Settings */}
        <Route path="/portal/site-settings" element={
          <PortalLayout>
            <SiteSettings />
          </PortalLayout>
        } />

        {/* Beta Features Pages */}
        <Route path="/portal/campusTimetableTeacher" element={
          <PortalLayout>
            <CampusTimetableTeacher />
          </PortalLayout>
        } />
        <Route path="/portal/timetable" element={
          <PortalLayout>
            <PortalTimetable />
          </PortalLayout>
        } />
        {/* Guide to both timetable pages, plus a live check of the seeded Test Demo data.
            Renders the real pages in tabs, so it doubles as the place to resize and judge
            the phone layout without hunting for a week that has anything in it. */}
        <Route path="/portal/timetable-demo" element={
          <PortalLayout>
            <PortalTimetableGuide />
          </PortalLayout>
        } />
      </Routes>
    </Router>
    </PortalRoleProvider>
    </>
  );
}

export default App;